import React from 'react';
import { Link } from 'react-router-dom';
import PageSEO from '../components/PageSEO';
import ContentLayout from '../components/ContentLayout';

const ToonFormatSpec: React.FC = () => (
  <>
    <PageSEO
      path="/toon-format-spec"
      title="TOON Format Spec: Syntax Rules and Examples"
      description="The TOON (Token-Oriented Object Notation) syntax reference: how objects, arrays, strings, numbers, booleans, and nesting are written, with examples for each rule."
      keywords="TOON spec, TOON syntax, TOON format rules, how to write TOON, TOON reference"
    />
    <ContentLayout
      related={[
        { to: '/what-is-toon', label: 'What is TOON? A plain-English guide' },
        { to: '/json-vs-toon', label: 'JSON vs TOON: a side-by-side comparison' },
        { to: '/reduce-llm-token-costs', label: 'How to reduce LLM token costs' },
      ]}
    >
      <h1>TOON format spec and syntax</h1>
      <p className="content-lead">
        TOON expresses any JSON value using indentation instead of brackets. This reference covers
        each rule with a short example. Conversion is lossless in both directions — valid JSON maps
        to TOON and back without changing a single value.
      </p>

      <h2>Objects</h2>
      <p>
        Keys are written one per line as <code>key: value</code>. Keys are unquoted when they are
        simple identifiers (letters, digits, underscores, hyphens) and quoted otherwise.
      </p>
      <pre><code>{`name: Ada
role: engineer
"display name": Ada Lovelace`}</code></pre>

      <h2>Nesting</h2>
      <p>A nested object goes on the following lines, indented by two spaces:</p>
      <pre><code>{`user:
  id: 1
  address:
    city: London
    zip: EC1A`}</code></pre>

      <h2>Arrays of objects</h2>
      <p>
        Each element starts with a dash. Fields of the same object align under it. This is where
        TOON saves the most versus JSON, because the field names are not repeated as punctuation.
      </p>
      <pre><code>{`items:
  - id: 1
    name: Pen
  - id: 2
    name: Pencil`}</code></pre>

      <h2>Simple (scalar) arrays</h2>
      <p>
        Short arrays of primitives are written inline in square brackets, space-separated. Longer
        ones break onto dashed lines.
      </p>
      <pre><code>{`tags: [red green blue]`}</code></pre>

      <h2>Strings</h2>
      <p>
        A string is left unquoted when it is a simple token. Strings containing spaces or special
        characters are wrapped in double quotes, and inner quotes are escaped with a backslash.
      </p>
      <pre><code>{`title: Hello
note: "needs a comma, so it is quoted"`}</code></pre>

      <h2>Numbers, booleans, and null</h2>
      <p>These are written bare, exactly as in JSON:</p>
      <pre><code>{`count: 42
ratio: 3.14
active: true
deleted: null`}</code></pre>

      <h2>Empty values</h2>
      <p>An empty object is <code>{'{}'}</code> and an empty array is <code>[]</code>.</p>

      <div className="content-callout">
        <p>
          <strong>Round-trip guarantee:</strong> the <Link to="/">converter</Link> turns JSON into
          TOON and TOON back into JSON. If a payload does not round-trip cleanly, that is a bug worth
          reporting — the formats are designed to be exactly equivalent.
        </p>
      </div>

      <h2>Convert without reading the spec</h2>
      <p>
        You rarely need to write TOON by hand. Paste JSON into the{' '}
        <Link to="/">converter</Link> and it produces valid TOON for you instantly, in your browser.
      </p>
    </ContentLayout>
  </>
);

export default ToonFormatSpec;
